import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function FeaturedCarousel({ jerseys = [], pickCount = 6 }) {
  // Show the most recently added jerseys (jerseys come sorted newest first)
  const featured = jerseys.slice(0, Math.min(pickCount, jerseys.length));

  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const slidesPerView = () => {
    if (typeof window === "undefined") return 4;
    if (window.innerWidth < 640) return 1;
    if (window.innerWidth < 1024) return 2;
    return 4;
  };

  const [perView, setPerView] = useState(slidesPerView());
  useEffect(() => {
    const onResize = () => setPerView(slidesPerView());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const maxIdx = Math.max(0, featured.length - perView);

  // Auto-rotate every 5s
  useEffect(() => {
    if (featured.length <= perView) return;
    const t = setInterval(() => {
      setIdx((i) => (i >= maxIdx ? 0 : i + 1));
    }, 5000);
    return () => clearInterval(t);
  }, [featured.length, perView, maxIdx]);

  if (!featured.length) return null;

  const go = (delta) => setIdx((i) => Math.max(0, Math.min(maxIdx, i + delta)));

  return (
    <div data-testid="featured-carousel" className="relative">
      <div className="overflow-hidden">
        <div
          ref={trackRef}
          className="flex transition-transform duration-700 ease-out"
          style={{ transform: `translateX(-${(idx * 100) / perView}%)` }}
        >
          {featured.map((j) => (
            <div
              key={j.id}
              className="flex-shrink-0 px-3"
              style={{ width: `${100 / perView}%` }}
            >
              <Link
                to={`/shop/${j.id}`}
                data-testid={`featured-${j.id}`}
                className="fz-card block group"
              >
                <div className="aspect-square overflow-hidden border-b border-[#332D2B] bg-[#2A2320]">
                  <img src={j.image_url} alt={j.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">{j.team} · {j.season}</div>
                  <div className="font-display text-lg text-[#F2EBDD] mt-1 leading-tight line-clamp-1">{j.name}</div>
                  <div className="flex items-center justify-between mt-3">
                    <div className="font-display text-xl text-[#C8A96A]">£{j.price}</div>
                    <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#A8A39D] uppercase">{j.stock > 0 ? "In Stock" : "Sold Out"}</div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>

      {featured.length > perView && (
        <>
          <button
            data-testid="carousel-prev"
            onClick={() => go(-1)}
            aria-label="Previous"
            className="absolute top-1/2 -translate-y-1/2 -left-3 lg:-left-5 w-10 h-10 bg-[#0F0C0B] border border-[#C8A96A] text-[#C8A96A] hover:bg-[#C8A96A] hover:text-[#0F0C0B] flex items-center justify-center transition-colors z-10"
            disabled={idx === 0}
          >
            <ChevronLeft size={18} />
          </button>
          <button
            data-testid="carousel-next"
            onClick={() => go(1)}
            aria-label="Next"
            className="absolute top-1/2 -translate-y-1/2 -right-3 lg:-right-5 w-10 h-10 bg-[#0F0C0B] border border-[#C8A96A] text-[#C8A96A] hover:bg-[#C8A96A] hover:text-[#0F0C0B] flex items-center justify-center transition-colors z-10"
            disabled={idx === maxIdx}
          >
            <ChevronRight size={18} />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-5">
            {Array.from({ length: maxIdx + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-1 transition-all ${i === idx ? "w-8 bg-[#C8A96A]" : "w-4 bg-[#332D2B] hover:bg-[#613123]"}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
